import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Clock } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

export default function SalonCard({ salon, theme = 'neutral' }) {
  const isMen = salon.category === 'men' || theme === 'men';
  
  const cardClass = isMen 
    ? 'bg-zinc-900 border border-zinc-800 hover:border-blue-500/50 rounded-none salon-card salon-card-men'
    : 'bg-white border border-pink-100 shadow-sm hover:shadow-lg hover:border-pink-200 rounded-2xl salon-card salon-card-women';

  const textClass = isMen ? 'text-zinc-100' : 'text-rose-900';
  const secondaryTextClass = isMen ? 'text-zinc-400' : 'text-rose-600';
  const priceClass = isMen ? 'text-blue-400' : 'text-pink-600';
  
  const buttonClass = isMen 
    ? 'bg-blue-600 hover:bg-blue-700 text-white rounded-none uppercase tracking-wider text-xs font-bold'
    : 'bg-pink-600 hover:bg-pink-700 text-white rounded-full font-serif italic';

  return (
    <div className={`${cardClass} overflow-hidden`} data-testid={`salon-card-${salon.salon_id}`}>
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img 
          src={salon.banner || salon.logo || 'https://images.unsplash.com/photo-1579643046891-3386be56709c?crop=entropy&cs=srgb&fm=jpg&q=85'} 
          alt={salon.name}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
        <div className={isMen ? 'image-overlay' : 'image-overlay-pink'} />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <Badge className={isMen 
            ? 'bg-blue-600/90 text-white border-0' 
            : 'bg-pink-600/90 text-white border-0'
          }>
            {salon.category === 'men' ? "Men's" : "Women's"}
          </Badge>
        </div>

        {/* Featured Badge */}
        {salon.is_featured && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-amber-500 text-white border-0">
              Featured
            </Badge>
          </div>
        )}

        {/* Rating */}
        <div className="absolute bottom-4 right-4 flex items-center gap-1 bg-black/60 px-2 py-1 rounded">
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
          <span className="text-white text-sm font-medium">{salon.rating?.toFixed(1) || '0.0'}</span>
          <span className="text-zinc-400 text-xs">({salon.review_count || 0})</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className={`text-lg font-bold ${textClass} mb-2 line-clamp-1`}>
          {salon.name}
        </h3>
        
        <p className={`text-sm ${secondaryTextClass} mb-3 line-clamp-2`}>
          {salon.description || 'Premium grooming services'}
        </p>

        <div className="flex items-center gap-2 mb-4">
          <MapPin className={`w-4 h-4 ${secondaryTextClass}`} />
          <span className={`text-sm ${secondaryTextClass}`}>{salon.city}</span>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-zinc-700/50">
          <div>
            <span className={`text-xs ${secondaryTextClass}`}>Starting from</span>
            <p className={`text-xl font-bold ${priceClass}`}>
              ${salon.starting_price || '25'}
            </p>
          </div>
          
          <Link to={`/salon/${salon.salon_id}`}>
            <Button className={buttonClass} data-testid={`view-salon-${salon.salon_id}`}>
              {isMen ? 'VIEW DETAILS' : 'View Details'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
